import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema, generateHowToSchema } from '../../utils/seo';

export default function InvoiceDemoPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  useSEO({
    title: 'Invoice Demo - MtaPlannerPro | See How It Works',
    description: 'Watch a step-by-step demo of MtaPlannerPro\'s AI-powered construction invoice generator. See how we transform job photos into professional quotes with real-time pricing.',
    keywords: 'invoice demo, construction invoice tutorial, AI invoice walkthrough, how to create construction quote, invoice generator demo, step by step invoice guide',
    canonical: `${siteUrl}/invoice-demo`,
    ogTitle: 'Invoice Demo - MtaPlannerPro | See How It Works',
    ogDescription: 'Watch a step-by-step demo of MtaPlannerPro\'s AI-powered construction invoice generator.',
    ogUrl: `${siteUrl}/invoice-demo`,
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema({
          name: 'Invoice Demo - MtaPlannerPro',
          description: 'Watch a step-by-step demo of MtaPlannerPro\'s AI-powered construction invoice generator.',
          url: '/invoice-demo'
        }),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Invoice Demo', url: '/invoice-demo' }
        ]),
        generateHowToSchema({
          name: 'How to Create a Construction Invoice with MtaPlannerPro',
          description: 'Learn how to generate professional construction invoices in 5 simple steps using AI-powered automation.',
          steps: [
            {
              name: 'Upload Job Photos',
              text: 'Upload images of completed work. Our AI analyzes them to extract job details, materials used, and scope of work.'
            },
            {
              name: 'AI Analysis',
              text: 'Our AI calculates labor costs, Bunnings material prices, and compares with market rates in your area.'
            },
            {
              name: 'Choose Pricing Mode',
              text: 'Select between fitting client budget or charging premium market rates based on top tradies in your area.'
            },
            {
              name: 'Enter Client Details',
              text: 'Enter client information or use saved contacts. Add your company branding for professional invoices.'
            },
            {
              name: 'Generate Invoice',
              text: 'AI generates a professional invoice with itemized breakdown, ready to download, print, or send directly.'
            }
          ]
        })
      ]
    }
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set page metadata
    document.title = 'Invoice Demo - MtaPlannerPro | See How It Works';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Watch a step-by-step demo of MtaPlannerPro\'s AI-powered construction invoice generator. See how we transform job photos into professional quotes with real-time pricing.');
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (previewRef.current) {
        const rect = previewRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / 50;
        const y = (e.clientY - rect.top - rect.height / 2) / 50;
        setMousePosition({ x, y });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const steps = [
    {
      id: 1,
      title: 'Upload Job Photos',
      subtitle: 'Drag & drop or browse',
      description: 'Upload images of completed work - our AI analyzes them to extract job details, materials used, and scope of work.',
      icon: 'ri-camera-line',
      time: '10 sec',
      preview: 'upload'
    },
    {
      id: 2,
      title: 'AI Analysis',
      subtitle: 'Smart pricing engine',
      description: 'Our AI calculates labor costs, Bunnings material prices, and compares with market rates in your area.',
      icon: 'ri-brain-line',
      time: '5 sec',
      preview: 'analysis'
    },
    {
      id: 3,
      title: 'Choose Pricing',
      subtitle: 'Budget or Market Rate',
      description: 'Select between fitting client budget or charging premium market rates based on top tradies in your area.',
      icon: 'ri-money-dollar-circle-line',
      time: '15 sec',
      preview: 'pricing'
    },
    {
      id: 4,
      title: 'Client Details',
      subtitle: 'Auto-fill available',
      description: 'Enter client information or use saved contacts. Add your company branding for professional invoices.',
      icon: 'ri-user-settings-line',
      time: '20 sec',
      preview: 'client'
    },
    {
      id: 5,
      title: 'Generate Invoice',
      subtitle: 'PDF ready in seconds',
      description: 'AI generates a professional invoice with itemized breakdown, ready to download, print, or send directly.',
      icon: 'ri-file-text-line',
      time: '3 sec',
      preview: 'invoice'
    }
  ];

  const features = [
    { icon: 'ri-image-ai-line', title: 'Photo Analysis', desc: 'AI reads job photos' },
    { icon: 'ri-store-3-line', title: 'Bunnings Prices', desc: 'Real 2024 pricing' },
    { icon: 'ri-map-pin-user-line', title: 'Area Rates', desc: 'Local market data' },
    { icon: 'ri-scales-3-line', title: 'Price Compare', desc: 'Budget vs Market' },
    { icon: 'ri-file-pdf-2-line', title: 'PDF Export', desc: 'Professional output' },
    { icon: 'ri-mail-send-line', title: 'Direct Send', desc: 'Email to clients' }
  ];

  const startDemo = () => {
    setIsPlaying(true);
    setCurrentStep(0);
    setShowResult(false);
    
    let step = 0;
    const interval = setInterval(() => {
      step++;
      if (step >= 5) {
        clearInterval(interval);
        setTimeout(() => {
          setShowResult(true);
          setIsPlaying(false);
        }, 1000);
      } else {
        setCurrentStep(step);
      }
    }, 2000);
  };

  const goToStep = (index: number) => {
    if (!isPlaying) {
      setCurrentStep(index);
      setShowResult(false);
    }
  };

  const resetDemo = () => {
    setCurrentStep(0);
    setShowResult(false);
    setIsPlaying(false);
  };

  const renderPreviewContent = () => {
    if (showResult) {
      return (
        <div className="animate-scaleIn">
          {/* Final Invoice Preview */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <i className="ri-building-2-line text-white text-2xl"></i>
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold text-lg">INVOICE</h3>
                    <p className="text-white/80 text-sm">INV-2024-0847</p>
                  </div>
                </div>
                <div className="text-right text-white">
                  <p className="text-sm text-white/80">Amount Due</p>
                  <p className="text-3xl font-bold">$4,238</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex justify-between text-sm">
                <div>
                  <p className="text-slate-500">Bill To</p>
                  <p className="font-semibold text-slate-900">John Smith</p>
                  <p className="text-slate-600">123 Collins St, Melbourne</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500">Due Date</p>
                  <p className="font-semibold text-amber-600">Jan 20, 2025</p>
                </div>
              </div>
              
              <div className="border-t border-b border-slate-100 py-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">HVAC Installation - Split System</span>
                  <span className="font-medium">$2,088</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Materials (Bunnings)</span>
                  <span className="font-medium">$1,850</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Electrical Work</span>
                  <span className="font-medium">$300</span>
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-4 pt-2">
                <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-200 transition-colors whitespace-nowrap">
                  <i className="ri-download-line"></i> Download PDF
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg text-white text-sm font-medium cursor-pointer hover:from-amber-600 hover:to-orange-700 transition-colors whitespace-nowrap">
                  <i className="ri-send-plane-line"></i> Send to Client
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-center gap-2 text-amber-500">
            <i className="ri-checkbox-circle-fill text-2xl"></i>
            <span className="font-semibold">Invoice Generated Successfully!</span>
          </div>
        </div>
      );
    }

    switch (steps[currentStep].preview) {
      case 'upload':
        return (
          <div className="space-y-4 animate-fadeIn">
            <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center bg-slate-50/50 hover:border-amber-400 transition-colors">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-upload-cloud-2-line text-amber-600 text-3xl"></i>
              </div>
              <p className="font-semibold text-slate-900 mb-1">Drop job photos here</p>
              <p className="text-sm text-slate-500">or click to browse</p>
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-slate-100 relative group animate-slideUp" style={{ animationDelay: `${i * 100}ms` }}>
                  <img 
                    src={`https://public.readdy.ai/ai/img_res/4658e9c73359717ff2d657a5d721ecf0.jpg$%7Bi%7D&width=200&height=200&seq=demo-upload-${i}&orientation=squarish`}
                    alt={`Job photo ${i}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-amber-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-check-line text-white text-2xl"></i>
                  </div>
                  <div className="absolute top-1 right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-amber-50 rounded-lg p-3">
              <i className="ri-information-line text-amber-600"></i>
              <span>4 photos uploaded • Ready for AI analysis</span>
            </div>
          </div>
        );
        
      case 'analysis':
        return (
          <div className="space-y-4 animate-fadeIn">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <i className="ri-brain-line text-amber-400 text-xl animate-pulse"></i>
                </div>
                <div>
                  <p className="font-semibold">AI Analyzing Job...</p>
                  <p className="text-sm text-slate-400">Processing 4 images</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {['Detecting job type...', 'Calculating materials...', 'Fetching Bunnings prices...', 'Comparing market rates...'].map((text, i) => (
                  <div key={i} className="flex items-center gap-3 animate-slideRight" style={{ animationDelay: `${i * 300}ms` }}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${i <= 2 ? 'bg-amber-500' : 'bg-slate-600'}`}>
                      {i <= 2 ? <i className="ri-check-line text-white text-xs"></i> : <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse"></div>}
                    </div>
                    <span className={`text-sm ${i <= 2 ? 'text-white' : 'text-slate-400'}`}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                <p className="text-xs text-slate-500 mb-1">Detected Category</p>
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <i className="ri-temp-cold-line text-amber-500"></i> HVAC Installation
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                <p className="text-xs text-slate-500 mb-1">Complexity</p>
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <i className="ri-bar-chart-line text-orange-500"></i> Moderate
                </p>
              </div>
            </div>
          </div>
        );
        
      case 'pricing':
        return (
          <div className="space-y-4 animate-fadeIn">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5 border-2 border-amber-500 shadow-lg relative">
                <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full">Selected</div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-wallet-3-line text-amber-600 text-2xl"></i>
                </div>
                <h4 className="font-bold text-slate-900 mb-1">Budget Mode</h4>
                <p className="text-xs text-slate-500 mb-3">Fit client's budget</p>
                <p className="text-2xl font-bold text-amber-600">$3,000</p>
                <p className="text-xs text-slate-500">Client's budget</p>
              </div>
              
              <div className="bg-white rounded-2xl p-5 border border-slate-200 opacity-60">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-vip-crown-line text-slate-400 text-2xl"></i>
                </div>
                <h4 className="font-bold text-slate-900 mb-1">Market Rate</h4>
                <p className="text-xs text-slate-500 mb-3">Top tradie rates</p>
                <p className="text-2xl font-bold text-slate-900">$4,238</p>
                <p className="text-xs text-slate-500">Premium pricing</p>
              </div>
            </div>
            
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-line text-orange-600 text-xl"></i>
                <div>
                  <p className="font-semibold text-orange-800 text-sm">Budget Mode Selected</p>
                  <p className="text-xs text-orange-700">Client saves $1,238 compared to market rate</p>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'client':
        return (
          <div className="space-y-4 animate-fadeIn">
            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Client Name</label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <i className="ri-user-line text-slate-400"></i>
                  <span className="text-slate-900">John Smith</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Email</label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <i className="ri-mail-line text-slate-400"></i>
                  <span className="text-slate-900">john.smith@email.com</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Address</label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <i className="ri-map-pin-line text-slate-400"></i>
                  <span className="text-slate-900">123 Collins St, Melbourne VIC</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <i className="ri-building-2-line text-white text-xl"></i>
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Your Company Logo</p>
                <p className="text-xs text-slate-500">Acme Services Pty Ltd</p>
              </div>
              <i className="ri-check-circle-fill text-amber-500 text-xl ml-auto"></i>
            </div>
          </div>
        );
        
      case 'invoice':
        return (
          <div className="flex items-center justify-center h-full animate-fadeIn">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                <i className="ri-file-text-line text-white text-3xl"></i>
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
              </div>
              <p className="font-semibold text-slate-900">Generating Invoice...</p>
              <p className="text-sm text-slate-500">Creating professional PDF</p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Navbar - Updated to match home page */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-slate-950/95 backdrop-blur-xl shadow-lg shadow-black/10 border-b border-white/5'
          : 'bg-slate-950/80 backdrop-blur-lg border-b border-white/5'
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25 group-hover:shadow-amber-500/40 transition-all duration-300 group-hover:scale-105">
                  <i className="ri-building-2-fill text-white text-xl"></i>
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-950 animate-pulse"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-lg leading-tight tracking-tight">
                  MtaPlannerPro
                </span>
                <span className="text-amber-400 text-[10px] font-semibold uppercase tracking-wider">
                  For Tradies
                </span>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/"
                className="relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap text-slate-300 hover:text-white hover:bg-white/5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-home-5-line text-base"></i>
                  Home
                </span>
              </Link>
              <Link
                to="/invoice"
                className="relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap text-slate-300 hover:text-white hover:bg-white/5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-file-text-line text-base"></i>
                  Quote Generator
                </span>
              </Link>
              <Link
                to="/invoice-demo"
                className="relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap text-white bg-white/10"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-play-circle-line text-base"></i>
                  Demo
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full border border-amber-500/30"></div>
              </Link>
            </div>
            <Link 
              to="/invoice" 
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
            >
              <i className="ri-arrow-right-line mr-2"></i>
              Try 7-Day Free Trial
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Updated theme */}
      <section className="pt-28 pb-12 px-6 relative">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-sm text-amber-400 rounded-full text-sm font-medium mb-6 border border-amber-500/30">
            <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
            Interactive Demo
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-sora">
            See How <span className="text-amber-600">MtaPlannerPro</span> Works
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
            Follow this step-by-step walkthrough to see how MtaPlannerPro transforms job site photos into professional construction invoices with AI-powered cost analysis and real material pricing.
          </p>
          
          {!isPlaying && !showResult && (
            <button
              onClick={startDemo}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-amber-500/30 transition-all cursor-pointer hover:scale-105"
            >
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-play-fill text-xl"></i>
              </div>
              Watch Demo
            </button>
          )}
          
          {isPlaying && (
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full text-white">
              <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse"></div>
              <span className="font-medium">Demo in progress...</span>
            </div>
          )}
        </div>
      </section>

      {/* Main Demo Section */}
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left - Steps Timeline */}
            <div className="lg:col-span-2 space-y-3">
              {steps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => goToStep(index)}
                  disabled={isPlaying}
                  className={`w-full text-left p-4 rounded-2xl transition-all duration-500 cursor-pointer ${
                    index === currentStep
                      ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/50 shadow-lg shadow-amber-500/10'
                      : index < currentStep
                        ? 'bg-white/5 border border-white/10'
                        : 'bg-white/5 border border-transparent hover:border-white/10'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                      index === currentStep
                        ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30'
                        : index < currentStep
                          ? 'bg-amber-500/20'
                          : 'bg-white/10'
                    }`}>
                      {index < currentStep ? (
                        <i className="ri-check-line text-amber-400 text-xl"></i>
                      ) : (
                        <i className={`${step.icon} text-xl ${index === currentStep ? 'text-white' : 'text-slate-400'}`}></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold uppercase tracking-wider ${
                          index === currentStep ? 'text-amber-400' : 'text-slate-500'
                        }`}>
                          Step {step.id}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          index === currentStep ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-slate-500'
                        }`}>
                          {step.time}
                        </span>
                      </div>
                      <h3 className={`font-bold mb-1 transition-colors ${
                        index <= currentStep ? 'text-white' : 'text-slate-400'
                      }`}>
                        {step.title}
                      </h3>
                      <p className={`text-sm transition-colors ${
                        index === currentStep ? 'text-slate-300' : 'text-slate-500'
                      }`}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
              
              {/* Progress Bar */}
              <div className="mt-6 p-4 bg-white/5 rounded-2xl border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Progress</span>
                  <span className="text-sm font-semibold text-amber-400">{Math.round(((currentStep + 1) / 5) * 100)}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transition-all duration-500"
                    style={{ width: `${((currentStep + 1) / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Right - Live Preview */}
            <div className="lg:col-span-3">
              <div 
                ref={previewRef}
                className="relative"
                style={{
                  transform: `perspective(1000px) rotateY(${mousePosition.x}deg) rotateX(${-mousePosition.y}deg)`,
                  transition: 'transform 0.1s ease-out'
                }}
              >
                {/* Device Frame */}
                <div className="bg-slate-800 rounded-3xl p-3 shadow-2xl shadow-black/50 border border-slate-700">
                  {/* Browser Header */}
                  <div className="bg-slate-700 rounded-t-xl px-4 py-3 flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                      <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                    </div>
                    <div className="flex-1 bg-slate-600 rounded-lg px-4 py-1.5 text-sm text-slate-400 flex items-center gap-2">
                      <i className="ri-lock-line text-emerald-400"></i>
                      buildinvoicepro.com/invoice
                    </div>
                  </div>
                  
                  {/* Screen Content */}
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-b-xl p-6 min-h-[480px]">
                    {renderPreviewContent()}
                  </div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-amber-500 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg shadow-amber-500/30 animate-bounce">
                  <i className="ri-magic-line mr-1"></i> AI Powered
                </div>
              </div>
              
              {/* Action Buttons */}
              {showResult && (
                <div className="mt-6 flex items-center justify-center gap-4 animate-fadeIn">
                  <button
                    onClick={resetDemo}
                    className="px-6 py-3 bg-white/10 text-white font-medium rounded-full hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap border border-white/10"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Restart Demo
                  </button>
                  <Link
                    to="/invoice"
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-line mr-2"></i>
                    Create Your Invoice
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Strip - Updated theme */}
      <section className="py-16 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Powerful Features</h2>
            <p className="text-slate-400">Everything you need to create professional invoices</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {features.map((feature, index) => (
              <button
                key={index}
                onMouseEnter={() => setActiveFeature(index)}
                className={`p-5 rounded-2xl text-center transition-all cursor-pointer ${
                  activeFeature === index
                    ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/50 scale-105'
                    : 'bg-white/5 border border-white/10 hover:border-white/20'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 transition-all ${
                  activeFeature === index ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-white/10'
                }`}>
                  <i className={`${feature.icon} text-xl ${activeFeature === index ? 'text-white' : 'text-slate-400'}`}></i>
                </div>
                <h4 className={`font-semibold text-sm mb-1 ${activeFeature === index ? 'text-white' : 'text-slate-300'}`}>
                  {feature.title}
                </h4>
                <p className="text-xs text-slate-500">{feature.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - Updated theme */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '90%', label: 'Time Saved', icon: 'ri-time-line' },
              { value: '2.5K+', label: 'Invoices Created', icon: 'ri-file-list-3-line' },
              { value: '$1.2M', label: 'Processed', icon: 'ri-money-dollar-circle-line' },
              { value: '4.9★', label: 'User Rating', icon: 'ri-star-line' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-amber-400 text-xl`}></i>
                </div>
                <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Updated theme */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl p-12 text-center overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Save Hours Every Week?
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Join thousands of tradies who create professional invoices in seconds. Get 5 free quotes & invoices with your 7-day trial.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/invoice"
                  className="px-8 py-4 bg-white text-amber-600 font-semibold rounded-full hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  Start Creating Invoices
                </Link>
                <Link
                  to="/"
                  className="px-8 py-4 bg-white/20 text-white font-semibold rounded-full hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap border border-white/30"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Updated to match home page */}
      <footer className="bg-slate-950 text-white py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                  <i className="ri-building-2-fill text-white text-xl"></i>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-lg leading-tight tracking-tight">
                  MtaPlannerPro
                </span>
                <span className="text-amber-400 text-[10px] font-semibold uppercase tracking-wider">
                  For Tradies
                </span>
              </div>
            </Link>
            <div className="flex items-center gap-8">
              <Link to="/" className="text-white/70 hover:text-white transition-colors cursor-pointer">Home</Link>
              <Link to="/invoice" className="text-white/70 hover:text-white transition-colors cursor-pointer">Quote Generator</Link>
              <Link to="/invoice-demo" className="text-white transition-colors cursor-pointer">Demo</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                <i className="ri-twitter-line"></i>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                <i className="ri-linkedin-line"></i>
              </a>
            </div>
          </div>
          <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">© 2025 MtaPlannerPro. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-white/50 hover:text-white/80 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideRight {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(0) translateX(20px); }
          75% { transform: translateY(20px) translateX(10px); }
        }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
        .animate-scaleIn { animation: scaleIn 0.5s ease-out forwards; }
        .animate-slideUp { animation: slideUp 0.5s ease-out forwards; }
        .animate-slideRight { animation: slideRight 0.5s ease-out forwards; }
        .animate-float { animation: float 8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
